/**
 * 로그 목록(§8.1). 행 가상화(보이는 행만 그림), 맨 아래 따라가기, 키보드 이동.
 *
 * - 위로 스크롤하면 따라가기를 멈추고 "새 항목 n건" 칩만 보인다. 위치를 빼앗지 않는다(ADV-H05).
 * - ↑↓ 행 이동, Home/End, Enter 상세, Esc 상세 닫기(ADV-L01). 포커스 링은 키보드 포커스에만.
 * - 표 규칙: 본문·헤더 13px Regular, 숫자 우측 정렬·tabular-nums, 긴 텍스트는 말줄임+툴팁.
 */
import { makeStyles, mergeClasses, tokens, Tooltip } from "@fluentui/react-components";
import { ArrowDown12Regular } from "@fluentui/react-icons";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { formatDate } from "@/lib/date-format";
import { KIND_LABEL, LEVEL_LABEL, peerLabel, STATUS_LABEL, type LogEntry } from "./log-model";

export const ROW_H = 28;
const OVERSCAN = 8;
const COLUMNS = "160px 52px 60px minmax(120px, 180px) minmax(96px, 150px) 72px 64px minmax(220px, 1fr)";

const useStyles = makeStyles({
  frame: {
    position: "relative",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    outlineStyle: "none",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  inner: { minWidth: "900px" },
  row: {
    display: "grid",
    gridTemplateColumns: COLUMNS,
    alignItems: "center",
    height: `${ROW_H}px`,
    boxSizing: "border-box",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  head: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  body: { position: "relative" },
  item: {
    position: "absolute",
    left: 0,
    right: 0,
    cursor: "default",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
  },
  selected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Selected },
  },
  cell: {
    padding: `0 ${tokens.spacingHorizontalS}`,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  time: { fontVariantNumeric: "tabular-nums", color: tokens.colorNeutralForeground2 },
  muted: { color: tokens.colorNeutralForeground3 },
  danger: { color: tokens.colorStatusDangerForeground1 },
  warning: { color: tokens.colorStatusWarningForeground1 },
  chip: {
    position: "absolute",
    bottom: tokens.spacingVerticalM,
    left: "50%",
    transform: "translateX(-50%)",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    height: "28px",
    padding: `0 ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    boxShadow: tokens.shadow8,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
});

function Clip({ text, className }: { text: string; className?: string }) {
  const styles = useStyles();
  if (!text) return <span className={mergeClasses(styles.cell, className)} role="gridcell" />;
  return (
    <Tooltip content={text} relationship="description" positioning="below-start" withArrow={false}>
      <span className={mergeClasses(styles.cell, className)} role="gridcell">
        {text}
      </span>
    </Tooltip>
  );
}

export function rowDomId(id: string): string {
  return `log-row-${id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

export interface LogTableProps {
  items: LogEntry[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onOpenDetail: () => void;
  onCloseDetail: () => void;
  /** 위쪽 끝에 닿으면 더 오래된 항목을 읽는다 */
  onReachTop?: () => void;
}

export function LogTable({ items, selectedId, onSelect, onOpenDetail, onCloseDetail, onReachTop }: LogTableProps) {
  const styles = useStyles();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [height, setHeight] = useState(400);
  const [follow, setFollow] = useState(true);
  const [seen, setSeen] = useState(items.length);
  const lastLen = useRef(items.length);
  const firstId = useRef(items[0]?.id);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeight(el.clientHeight));
    ro.observe(el);
    setHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  // 따라가는 중이면 새 항목이 붙을 때 맨 아래로. 위에 이전 항목이 붙으면(오래된 페이지) 보던 자리를 지킨다.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const prepended = firstId.current !== items[0]?.id && items.length > lastLen.current && items.findIndex((e) => e.id === firstId.current) > 0;
    if (prepended) {
      const added = items.findIndex((e) => e.id === firstId.current);
      el.scrollTop += added * ROW_H;
    } else if (follow) {
      el.scrollTop = el.scrollHeight;
      setSeen(items.length);
    }
    firstId.current = items[0]?.id;
    lastLen.current = items.length;
  }, [items, follow]);

  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setScrollTop(el.scrollTop);
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < ROW_H * 1.5;
    setFollow((prev) => {
      if (prev && !atBottom) setSeen(items.length);
      return atBottom;
    });
    if (el.scrollTop < ROW_H * 4) onReachTop?.();
  }, [items.length, onReachTop]);

  const jumpToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setFollow(true);
    setSeen(items.length);
  };

  const selectedIndex = selectedId ? items.findIndex((e) => e.id === selectedId) : -1;

  const moveTo = (index: number) => {
    if (!items.length) return;
    const i = Math.max(0, Math.min(items.length - 1, index));
    onSelect(items[i]!.id);
    const el = scrollerRef.current;
    if (!el) return;
    const top = i * ROW_H;
    const headH = ROW_H;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + ROW_H > el.scrollTop + el.clientHeight - headH) el.scrollTop = top + ROW_H - el.clientHeight + headH;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const page = Math.max(1, Math.floor(height / ROW_H) - 1);
    const cur = selectedIndex < 0 ? items.length : selectedIndex;
    if (e.key === "ArrowDown") moveTo(selectedIndex < 0 ? items.length - 1 : cur + 1);
    else if (e.key === "ArrowUp") moveTo(selectedIndex < 0 ? items.length - 1 : cur - 1);
    else if (e.key === "PageDown") moveTo(cur + page);
    else if (e.key === "PageUp") moveTo(cur - page);
    else if (e.key === "Home") moveTo(0);
    else if (e.key === "End") moveTo(items.length - 1);
    else if (e.key === "Enter" && selectedIndex >= 0) onOpenDetail();
    else if (e.key === "Escape") onCloseDetail();
    else return;
    e.preventDefault();
  };

  const bodyTop = Math.max(0, scrollTop - ROW_H);
  const start = Math.max(0, Math.floor(bodyTop / ROW_H) - OVERSCAN);
  const end = Math.min(items.length, Math.ceil((bodyTop + height) / ROW_H) + OVERSCAN);
  const newCount = follow ? 0 : Math.max(0, items.length - seen);

  return (
    <div className={styles.frame}>
      <div
        ref={scrollerRef}
        className={styles.scroller}
        role="grid"
        aria-label="로그 목록"
        aria-rowcount={items.length + 1}
        aria-activedescendant={selectedIndex >= 0 ? rowDomId(items[selectedIndex]!.id) : undefined}
        tabIndex={0}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
      >
        <div className={styles.inner}>
          <div className={mergeClasses(styles.row, styles.head)} role="row" aria-rowindex={1}>
            {["시각", "수준", "종류", "이름", "상대", "상태", "소요", "요약"].map((h) => (
              <span key={h} role="columnheader" className={mergeClasses(styles.cell, h === "소요" && styles.num)}>
                {h}
              </span>
            ))}
          </div>
          <div className={styles.body} style={{ height: items.length * ROW_H }}>
            {items.slice(start, end).map((e, k) => {
              const i = start + k;
              return (
                <div
                  key={e.id}
                  id={rowDomId(e.id)}
                  role="row"
                  aria-rowindex={i + 2}
                  aria-selected={e.id === selectedId}
                  className={mergeClasses(styles.row, styles.item, e.id === selectedId && styles.selected)}
                  style={{ top: i * ROW_H }}
                  onClick={() => {
                    onSelect(e.id);
                    onOpenDetail();
                  }}
                >
                  <span role="gridcell" className={mergeClasses(styles.cell, styles.time)}>
                    {formatDate(e.ts, "logTime")}
                  </span>
                  <span role="gridcell" className={mergeClasses(styles.cell, e.level === "error" ? styles.danger : e.level === "warn" ? styles.warning : e.level === "debug" && styles.muted)}>
                    {LEVEL_LABEL[e.level]}
                  </span>
                  <span role="gridcell" className={styles.cell}>
                    {KIND_LABEL[e.type]}
                  </span>
                  <Clip text={e.action} />
                  <Clip text={peerLabel(e)} className={e.peer ? undefined : styles.muted} />
                  <span role="gridcell" className={mergeClasses(styles.cell, e.status === "busy" ? styles.warning : e.status !== "ok" && styles.danger)}>
                    {STATUS_LABEL[e.status]}
                  </span>
                  <span role="gridcell" className={mergeClasses(styles.cell, styles.num, e.durationMs == null && styles.muted)}>
                    {e.durationMs == null ? "—" : `${e.durationMs}ms`}
                  </span>
                  <Clip text={e.summary} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {newCount > 0 && (
        <button type="button" className={styles.chip} onClick={jumpToBottom}>
          새 항목 {newCount.toLocaleString("ko-KR")}건
          <ArrowDown12Regular aria-hidden />
        </button>
      )}
    </div>
  );
}
